// The signed-in frame: the kit's AppLayout with the Console's navigation, the
// balance tile that opens Credits, the account menu, and the page. See Design >
// Names and definitions, "Console", and Design > UI.

import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { AccountMenu, AppLayout, Notice, Pointer, StatusBadge, useTheme } from "../../../ui/src/index.ts";
import type { AppLink, NavigationEntry, NavigationItem } from "../../../ui/src/index.ts";
import { waitingOn } from "../domain/lesson.ts";
import { usd } from "../domain/money.ts";
import { openCredits, signOut } from "./actions.ts";
import { href, type Route } from "./router.ts";
import { markSeen } from "./setup.ts";
import { useStore } from "./store.ts";

const navigation = (route: Route): NavigationEntry[] => [
  { label: "Home", icon: "home", href: href("home"), current: route.name === "home" },
  { label: "Playground", icon: "playground", href: href("playground"), current: route.name === "playground" },
  { label: "Usage", icon: "usage", href: href("usage"), current: route.name === "usage" },
  { label: "API keys", icon: "keys", href: href("keys"), unavailable: "Not connected in this prototype" },
];
const FOOT: NavigationItem[] = [{ label: "Documentation", icon: "docs", href: "https://docs.warmhub.ai", external: true }];
// The wordmark's app switcher. The Console can't tell yet whether you curate a hub, so it offers Studio to everyone.
const apps = (studioUrl: string): AppLink[] => [
  { name: "Console", icon: "questions", href: "/", description: "Ask questions and see what answers cost", current: true },
  { name: "Curator Studio", icon: "hub", href: studioUrl, description: "For curators: your hubs' earnings, questions and demand" },
];

export function Shell({ route, children }: { route: Route; children: ComponentChildren }) {
  const label = useStore((s) => s.config.label);
  const studioUrl = useStore((s) => s.config.studioUrl);
  const balance = useStore((s) => s.balance);
  const user = useStore((s) => s.user);
  const notice = useStore((s) => s.notice);
  const creditsOpen = useStore((s) => s.creditsOpen);
  // Home's "See what you were charged" step is done once Credits has been open, from anywhere.
  useEffect(() => { if (creditsOpen) markSeen(user?.account, "credits"); }, [creditsOpen, user?.account]);
  const hot = useStore((s) => {
    const l = s.lesson ? s.content.lessons.find((x) => x.id === s.lesson?.lessonId) : null;
    return !!l && !!s.lesson && waitingOn(l, s.lesson, "credits");
  });
  const [theme, setTheme] = useTheme();
  const name = user?.email ?? "Signed in";
  // Home and the Playground are working pages (option A): they draw their own bar and docked panel edge to edge.
  const flush = route.name !== "usage";
  return (
    <AppLayout product="Console" density="compact" flush={flush} navigation={navigation(route)} foot={FOOT} apps={apps(studioUrl)}
      context={label ? <StatusBadge>{label}</StatusBadge> : undefined}
      utility={
        <Pointer active={hot}>
          <button type="button" class="cs-balance" onClick={openCredits} aria-label={`Balance ${usd(balance)}: open Credits`}>
            <span class="cs-balance__label">Balance</span>
            <strong>{usd(balance)}</strong>
            <span class="cs-balance__action">{balance === null ? "Updates after a request" : "Top off credits"}</span>
          </button>
        </Pointer>
      }
      account={<AccountMenu name={name} theme={theme} onThemeChange={setTheme}
        actions={[{ label: "Credits", icon: "credits", onSelect: openCredits }, { label: "Sign out", icon: "signOut", onSelect: signOut }]} />}>
      {notice && <div class="cs-notice"><Notice tone="warning" title={notice} live /></div>}
      {children}
    </AppLayout>
  );
}
